import React, { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Mail,
  Users,
  Send,
  Filter,
  Eye,
  Loader2,
  CheckCircle,
  X,
  Calendar,
  Activity,
  Crown,
  UserCheck,
  AlertCircle,
  TrendingUp,
  Clock,
  Target,
  Sparkles,
  ChevronDown,
  ChevronUp,
  FileText,
  RefreshCw
} from 'lucide-react';
import EmailEditor from '../EmailEditor';

const BulkEmailCampaign = ({ onClose, onCampaignSent }) => {
  // Campaign state
  const [campaignName, setCampaignName] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('<p>Hi {{first_name}},</p><p>We have exciting news to share with you!</p>');
  const [testMode, setTestMode] = useState(false);
  
  // Schedule state
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' or 'scheduled'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  
  // Segment state
  const [segmentType, setSegmentType] = useState('all');
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [activeInDays, setActiveInDays] = useState('');
  const [inactiveForDays, setInactiveForDays] = useState('');
  const [signedUpAfter, setSignedUpAfter] = useState('');
  const [signedUpBefore, setSignedUpBefore] = useState('');
  const [onboardingComplete, setOnboardingComplete] = useState(null);
  const [excludeB2B, setExcludeB2B] = useState(true);
  
  // Preview state
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Sending state
  const [sending, setSending] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  const tiers = [
    { value: 'free', label: 'Free', icon: Users, color: '#9CA3AF' },
    { value: 'founding_member', label: 'Founding Member', icon: Crown, color: '#FFD700' },
    { value: 'premium', label: 'Premium', icon: Sparkles, color: '#df1283' }
  ];

  const segmentTypes = [
    { value: 'all', label: 'All Users', icon: Users, description: 'Send to everyone' },
    { value: 'tier', label: 'By Membership', icon: Crown, description: 'Filter by tier' },
    { value: 'activity', label: 'By Activity', icon: Activity, description: 'Active or inactive users' },
    { value: 'signup', label: 'By Signup Date', icon: Calendar, description: 'When they joined' },
    { value: 'onboarding', label: 'By Onboarding', icon: UserCheck, description: 'Completion status' }
  ];

  // Check if segment criteria are valid for sending
  const isSegmentValid = useCallback(() => {
    if (segmentType === 'all') return true;
    if (segmentType === 'tier') return selectedTiers.length > 0;
    if (segmentType === 'activity') return activeInDays || inactiveForDays;
    if (segmentType === 'signup') return signedUpAfter || signedUpBefore;
    if (segmentType === 'onboarding') return onboardingComplete !== null;
    return false;
  }, [segmentType, selectedTiers, activeInDays, inactiveForDays, signedUpAfter, signedUpBefore, onboardingComplete]);

  // Check if schedule time is valid (in the future)
  const isScheduleValid = useCallback(() => {
    if (scheduleMode !== 'scheduled') return true;
    if (!scheduledDate || !scheduledTime) return false;
    const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
    return scheduledDateTime > new Date();
  }, [scheduleMode, scheduledDate, scheduledTime]);

  // Get schedule validation message
  const getScheduleValidationMessage = useCallback(() => {
    if (scheduleMode !== 'scheduled') return null;
    if (!scheduledDate || !scheduledTime) return 'Select date and time';
    const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
    if (scheduledDateTime <= new Date()) return 'Schedule time must be in the future';
    return null;
  }, [scheduleMode, scheduledDate, scheduledTime]);

  // Get segment validation message
  const getSegmentValidationMessage = useCallback(() => {
    if (segmentType === 'tier' && selectedTiers.length === 0) {
      return 'Select at least one membership tier';
    }
    if (segmentType === 'activity' && !activeInDays && !inactiveForDays) {
      return 'Enter active or inactive days';
    }
    if (segmentType === 'signup' && !signedUpAfter && !signedUpBefore) {
      return 'Select at least one date';
    }
    if (segmentType === 'onboarding' && onboardingComplete === null) {
      return 'Select onboarding status';
    }
    return null;
  }, [segmentType, selectedTiers, activeInDays, inactiveForDays, signedUpAfter, signedUpBefore, onboardingComplete]);

  const buildSegment = useCallback(() => {
    const segment = {
      segment_type: segmentType,
      exclude_b2b: excludeB2B
    };

    if (segmentType === 'tier' && selectedTiers.length > 0) {
      segment.tiers = selectedTiers;
    }

    if (segmentType === 'activity') {
      if (activeInDays) segment.active_in_days = parseInt(activeInDays);
      if (inactiveForDays) segment.inactive_for_days = parseInt(inactiveForDays);
    }

    if (segmentType === 'signup') {
      if (signedUpAfter) segment.signed_up_after = signedUpAfter;
      if (signedUpBefore) segment.signed_up_before = signedUpBefore;
    }

    if (segmentType === 'onboarding' && onboardingComplete !== null) {
      segment.onboarding_complete = onboardingComplete;
    }

    return segment;
  }, [segmentType, selectedTiers, activeInDays, inactiveForDays, signedUpAfter, signedUpBefore, onboardingComplete, excludeB2B]);

  const fetchPreview = useCallback(async () => {
    // Don't fetch preview if segment criteria are invalid
    if (!isSegmentValid()) {
      setPreview(null);
      return;
    }
    
    setLoadingPreview(true);
    try {
      const segment = buildSegment();
      const res = await axios.post(`${API}/admin/email/segment-preview`, segment);
      setPreview(res.data);
    } catch (error) {
      console.error('Preview error:', error);
      // Handle validation errors gracefully
      if (error.response?.status === 422) {
        setPreview(null);
      } else {
        toast.error('Failed to preview segment');
      }
    } finally {
      setLoadingPreview(false);
    }
  }, [buildSegment, isSegmentValid]);

  // Fetch preview when segment changes
  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchPreview();
    }, 500);
    return () => clearTimeout(debounce);
  }, [segmentType, selectedTiers, activeInDays, inactiveForDays, signedUpAfter, signedUpBefore, onboardingComplete, excludeB2B, fetchPreview]);

  // Poll for campaign status
  useEffect(() => {
    if (activeCampaign && activeCampaign.status === 'sending') {
      const interval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/admin/email/campaign/${activeCampaign.campaign_id}/status`);
          setActiveCampaign(res.data);
          
          if (res.data.status === 'completed' || res.data.status === 'failed') {
            clearInterval(interval);
            setPollingInterval(null);
            if (res.data.status === 'completed') {
              toast.success(`Campaign completed! ${res.data.sent_count} emails sent.`);
              onCampaignSent?.();
            } else {
              toast.error(`Campaign failed: ${res.data.error || 'Unknown error'}`);
            }
          }
        } catch (error) {
          console.error('Status poll error:', error);
        }
      }, 2000);
      
      setPollingInterval(interval);
      return () => clearInterval(interval);
    }
  }, [activeCampaign?.campaign_id, activeCampaign?.status, onCampaignSent]);

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject line');
      return;
    }
    if (!bodyHtml.trim() || bodyHtml === '<p></p>') {
      toast.error('Please enter email content');
      return;
    }
    if (!preview || preview.total_recipients === 0) {
      toast.error('No recipients match this segment');
      return;
    }
    
    // Validate schedule if scheduled mode
    let scheduleFor = null;
    if (scheduleMode === 'scheduled') {
      if (!scheduledDate || !scheduledTime) {
        toast.error('Please select both date and time for scheduled campaign');
        return;
      }
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        toast.error('Scheduled time must be in the future');
        return;
      }
      scheduleFor = scheduledDateTime.toISOString();
    }

    setSending(true);
    try {
      const segment = buildSegment();
      const res = await axios.post(`${API}/admin/email/bulk-send`, {
        subject,
        body_html: bodyHtml,
        segment,
        campaign_name: campaignName || undefined,
        test_mode: testMode,
        schedule_for: scheduleFor
      });

      if (res.data.success) {
        if (res.data.status === 'scheduled') {
          toast.success(`Campaign scheduled for ${new Date(res.data.scheduled_for).toLocaleString()}`);
          onCampaignSent?.();
          onClose?.();
        } else {
          toast.success(`Campaign started: Sending to ${res.data.total_recipients} recipients`);
          setActiveCampaign(res.data);
        }
      } else {
        toast.error(res.data.error || 'Failed to start campaign');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const toggleTier = (tier) => {
    if (selectedTiers.includes(tier)) {
      setSelectedTiers(selectedTiers.filter(t => t !== tier));
    } else {
      setSelectedTiers([...selectedTiers, tier]);
    }
  };

  // Show campaign progress if active
  if (activeCampaign) {
    return (
      <div data-testid="bulk-email-campaign-progress" className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-lg p-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center">
              {activeCampaign.status === 'sending' ? (
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              ) : activeCampaign.status === 'completed' ? (
                <CheckCircle className="w-8 h-8 text-white" />
              ) : (
                <AlertCircle className="w-8 h-8 text-white" />
              )}
            </div>
            
            <h3 className="text-xl font-bold text-white mb-2">
              {activeCampaign.campaign_name || 'Email Campaign'}
            </h3>
            
            <p data-testid="bulk-campaign-status" className="text-white/60 mb-4">
              {activeCampaign.status === 'sending' ? 'Sending emails...' :
               activeCampaign.status === 'completed' ? 'Campaign completed!' :
               'Campaign failed'}
            </p>
            
            {/* Progress bar */}
            <div className="bg-[#4A0A66]/30 rounded-full h-3 mb-4 overflow-hidden">
              <div 
                data-testid="bulk-campaign-progress-bar"
                role="progressbar"
                aria-label="Campaign progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={activeCampaign.progress_percent || 0}
                className="h-full bg-gradient-to-r from-[#df1283] to-[#FF7901] transition-all duration-500"
                style={{ width: `${activeCampaign.progress_percent || 0}%` }}
              />
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-[#4A0A66]/20 rounded-lg p-3">
                <div data-testid="bulk-campaign-sent-count" className="text-2xl font-bold text-[#0FFFE2]">{activeCampaign.sent_count || 0}</div>
                <div className="text-xs text-white/50">Sent</div>
              </div>
              <div className="bg-[#4A0A66]/20 rounded-lg p-3">
                <div data-testid="bulk-campaign-failed-count" className="text-2xl font-bold text-[#FF7901]">{activeCampaign.failed_count || 0}</div>
                <div className="text-xs text-white/50">Failed</div>
              </div>
              <div className="bg-[#4A0A66]/20 rounded-lg p-3">
                <div data-testid="bulk-campaign-total-count" className="text-2xl font-bold text-white">{activeCampaign.total_recipients || 0}</div>
                <div className="text-xs text-white/50">Total</div>
              </div>
            </div>
            
            {activeCampaign.status !== 'sending' && (
              <Button
                data-testid="bulk-campaign-progress-close"
                onClick={() => {
                  setActiveCampaign(null);
                  onClose?.();
                }}
                className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white"
              >
                Close
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="bulk-email-campaign-modal" className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-5xl mb-8">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]/50 bg-[#1a0527]/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Bulk Email Campaign</h3>
              <p className="text-xs text-white/50">Send targeted emails to segmented audiences</p>
            </div>
          </div>
          <button 
            data-testid="bulk-email-campaign-close"
            aria-label="Close bulk email campaign"
            onClick={onClose}
            className="p-2 text-white/50 hover:text-white hover:bg-[#4A0A66]/50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Left: Segmentation & Compose */}
          <div className="flex-1 p-6 border-r border-[#4A0A66]/30 space-y-6">
            {/* Campaign Name */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                <FileText className="w-4 h-4 text-[#0FFFE2]" />
                Campaign Name (Optional)
              </label>
              <Input
                data-testid="bulk-campaign-name-input"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g., December Newsletter"
                className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
              />
            </div>

            {/* Audience Segmentation */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-white mb-3">
                <Filter className="w-4 h-4 text-[#FF7901]" />
                Target Audience
              </label>
              
              {/* Segment Type Selector */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
                {segmentTypes.map((type) => (
                  <button
                    key={type.value}
                    data-testid={`bulk-segment-${type.value}`}
                    onClick={() => setSegmentType(type.value)}
                    className={`p-3 rounded-lg border transition-all text-left ${
                      segmentType === type.value
                        ? 'bg-[#df1283]/20 border-[#df1283] text-white'
                        : 'bg-[#4A0A66]/20 border-[#4A0A66]/50 text-white/70 hover:border-[#4A0A66]'
                    }`}
                  >
                    <type.icon className={`w-4 h-4 mb-1 ${segmentType === type.value ? 'text-[#df1283]' : 'text-white/50'}`} />
                    <div className="text-xs font-medium truncate">{type.label}</div>
                  </button>
                ))}
              </div>

              {/* Segment Options */}
              <div className="bg-[#4A0A66]/10 rounded-lg p-4 space-y-4">
                {segmentType === 'tier' && (
                  <div>
                    <p className="text-sm text-white/60 mb-3">Select membership tiers:</p>
                    <div className="flex flex-wrap gap-2">
                      {tiers.map((tier) => (
                        <button
                          key={tier.value}
                          data-testid={`bulk-tier-${tier.value}`}
                          onClick={() => toggleTier(tier.value)}
                          className={`px-3 py-2 rounded-lg border flex items-center gap-2 transition-all ${
                            selectedTiers.includes(tier.value)
                              ? 'bg-[#df1283]/20 border-[#df1283]'
                              : 'bg-[#4A0A66]/20 border-[#4A0A66]/50 hover:border-[#4A0A66]'
                          }`}
                        >
                          <tier.icon className="w-4 h-4" style={{ color: tier.color }} />
                          <span className="text-sm text-white">{tier.label}</span>
                          {selectedTiers.includes(tier.value) && (
                            <CheckCircle className="w-4 h-4 text-[#0FFFE2]" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {segmentType === 'activity' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-white/60 mb-2 block">Active in last (days)</label>
                      <Input
                        data-testid="bulk-active-in-days"
                        type="number"
                        min="1"
                        value={activeInDays}
                        onChange={(e) => { setActiveInDays(e.target.value); setInactiveForDays(''); }}
                        placeholder="e.g., 30"
                        className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-white/60 mb-2 block">Inactive for (days)</label>
                      <Input
                        data-testid="bulk-inactive-for-days"
                        type="number"
                        min="1"
                        value={inactiveForDays}
                        onChange={(e) => { setInactiveForDays(e.target.value); setActiveInDays(''); }}
                        placeholder="e.g., 90"
                        className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                      />
                    </div>
                  </div>
                )}

                {segmentType === 'signup' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-white/60 mb-2 block">Signed up after</label>
                      <Input
                        data-testid="bulk-signed-up-after"
                        type="date"
                        value={signedUpAfter}
                        onChange={(e) => setSignedUpAfter(e.target.value)}
                        className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-white/60 mb-2 block">Signed up before</label>
                      <Input
                        data-testid="bulk-signed-up-before"
                        type="date"
                        value={signedUpBefore}
                        onChange={(e) => setSignedUpBefore(e.target.value)}
                        className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                      />
                    </div>
                  </div>
                )}

                {segmentType === 'onboarding' && (
                  <div>
                    <p className="text-sm text-white/60 mb-3">Onboarding status:</p>
                    <div className="flex gap-2">
                      <button
                        data-testid="bulk-onboarding-completed"
                        onClick={() => setOnboardingComplete(true)}
                        className={`px-4 py-2 rounded-lg border flex items-center gap-2 ${
                          onboardingComplete === true
                            ? 'bg-[#0FFFE2]/20 border-[#0FFFE2] text-[#0FFFE2]'
                            : 'bg-[#4A0A66]/20 border-[#4A0A66]/50 text-white/70'
                        }`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Completed
                      </button>
                      <button
                        data-testid="bulk-onboarding-incomplete"
                        onClick={() => setOnboardingComplete(false)}
                        className={`px-4 py-2 rounded-lg border flex items-center gap-2 ${
                          onboardingComplete === false
                            ? 'bg-[#FF7901]/20 border-[#FF7901] text-[#FF7901]'
                            : 'bg-[#4A0A66]/20 border-[#4A0A66]/50 text-white/70'
                        }`}
                      >
                        <Clock className="w-4 h-4" />
                        Incomplete
                      </button>
                    </div>
                  </div>
                )}

                {segmentType === 'all' && (
                  <p className="text-sm text-white/60">
                    This will send to all active users who have not unsubscribed.
                  </p>
                )}
              </div>

              {/* Advanced Options Toggle */}
              <button
                data-testid="bulk-advanced-options"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-white/50 hover:text-white mt-2"
              >
                {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Advanced Options
              </button>

              {showAdvanced && (
                <div className="bg-[#4A0A66]/10 rounded-lg p-4 mt-2 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">Exclude B2B Users</p>
                      <p className="text-xs text-white/50">B2B users should receive emails from their portal</p>
                    </div>
                    <Switch
                      data-testid="bulk-exclude-b2b-switch"
                      checked={excludeB2B}
                      onCheckedChange={setExcludeB2B}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">Test Mode</p>
                      <p className="text-xs text-white/50">Only send to first 5 recipients</p>
                    </div>
                    <Switch
                      data-testid="bulk-test-mode-switch"
                      checked={testMode}
                      onCheckedChange={setTestMode}
                    />
                  </div>
                </div>
              )}

            {/* Schedule Section */}
            <div className="bg-[#4A0A66]/10 rounded-lg p-4">
              <label className="flex items-center gap-2 text-sm font-medium text-white mb-3">
                <Calendar className="w-4 h-4 text-[#0FFFE2]" />
                When to Send
              </label>
              <div className="flex gap-2 mb-3">
                <button
                  data-testid="bulk-schedule-now"
                  onClick={() => setScheduleMode('now')}
                  className={`flex-1 px-4 py-2 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                    scheduleMode === 'now'
                      ? 'bg-[#df1283]/20 border-[#df1283] text-white'
                      : 'bg-[#4A0A66]/20 border-[#4A0A66]/50 text-white/70 hover:border-[#4A0A66]'
                  }`}
                >
                  <Send className="w-4 h-4" />
                  Send Now
                </button>
                <button
                  data-testid="bulk-schedule-later"
                  onClick={() => setScheduleMode('scheduled')}
                  className={`flex-1 px-4 py-2 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                    scheduleMode === 'scheduled'
                      ? 'bg-[#0FFFE2]/20 border-[#0FFFE2] text-white'
                      : 'bg-[#4A0A66]/20 border-[#4A0A66]/50 text-white/70 hover:border-[#4A0A66]'
                  }`}
                >
                  <Clock className="w-4 h-4" />
                  Schedule
                </button>
              </div>
              
              {scheduleMode === 'scheduled' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Date</label>
                    <Input
                      data-testid="bulk-schedule-date"
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/60 mb-1 block">Time</label>
                    <Input
                      data-testid="bulk-schedule-time"
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                    />
                  </div>
                  {scheduledDate && scheduledTime && (
                    <div className="col-span-2 text-xs text-[#0FFFE2] flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Will send on {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
            </div>
            </div>

            {/* Subject Line */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                <Mail className="w-4 h-4 text-[#df1283]" />
                Subject Line
              </label>
              <Input
                data-testid="bulk-subject-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g., Exciting news from UnCloned!"
                className="bg-[#4A0A66]/30 border-[#4A0A66] text-white h-12"
              />
              <p className="text-xs text-white/40 mt-1">
                Use {"{{first_name}}"} for personalization
              </p>
            </div>

            {/* Email Content */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                <FileText className="w-4 h-4 text-[#FF7901]" />
                Email Content
              </label>
              <div data-testid="bulk-email-content-editor">
                <EmailEditor
                  value={bodyHtml}
                  onChange={setBodyHtml}
                  placeholder="Hi {{first_name}}, We have exciting news..."
                />
              </div>
            </div>
          </div>

          {/* Right: Preview Panel */}
          <div className="w-full lg:w-96 p-6 bg-[#1a0527]/50">
            <div className="sticky top-0 space-y-4">
              {/* Live Email Preview - Matches actual email wrapper */}
              <div className="rounded-xl overflow-hidden shadow-lg" style={{ backgroundColor: '#f5f5f5' }}>
                <div className="bg-gradient-to-r from-[#df1283] to-[#FF7901] px-4 py-2 flex items-center gap-2">
                  <Eye className="w-4 h-4 text-white" />
                  <span className="text-sm font-medium text-white">Live Email Preview</span>
                </div>
                
                {/* Email Container - Mimics actual email */}
                <div className="p-3 max-h-[350px] overflow-y-auto" style={{ backgroundColor: '#f5f5f5' }}>
                  <div style={{ 
                    maxWidth: '100%', 
                    backgroundColor: '#ffffff', 
                    borderRadius: '12px', 
                    overflow: 'hidden',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    {/* Header with logo */}
                    <div style={{ 
                      background: 'linear-gradient(135deg, #1a0527 0%, #2D063E 100%)', 
                      padding: '20px', 
                      textAlign: 'center' 
                    }}>
                      <img 
                        src="https://customer-assets-jt897jd0.emergentagent.net/job_daily-notes-84/artifacts/7qbqgk18_UnCloned%C2%AE%20Journal-Color%20Logo.png" 
                        alt="UnCloned® Journal" 
                        style={{ maxWidth: '160px', height: 'auto', display: 'inline-block' }} 
                      />
                    </div>
                    
                    {/* Accent bar */}
                    <div style={{ height: '3px', background: 'linear-gradient(90deg, #df1283, #FF7901)' }}></div>
                    
                    {/* Subject line preview */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>
                      <p style={{ margin: 0, fontSize: '11px', color: '#999', marginBottom: '2px' }}>Subject:</p>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#333' }}>
                        {subject || '(No subject)'}
                      </p>
                    </div>
                    
                    {/* Main content */}
                    <div 
                      className="email-preview-content"
                      style={{ 
                        padding: '16px', 
                        color: '#333333', 
                        fontSize: '13px', 
                        lineHeight: '1.6',
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: `
                          <style>
                            .email-preview-content p { margin: 0 0 1em 0; }
                            .email-preview-content p:last-child { margin-bottom: 0; }
                            .email-preview-content br { display: block; content: ""; margin-top: 0.5em; }
                            .email-preview-content h1 { font-size: 1.5em; font-weight: bold; margin: 0.5em 0; color: #df1283; }
                            .email-preview-content h2 { font-size: 1.25em; font-weight: bold; margin: 0.5em 0; color: #333; }
                            .email-preview-content ul, .email-preview-content ol { padding-left: 1.5em; margin: 0.5em 0; }
                            .email-preview-content li { margin: 0.25em 0; }
                            .email-preview-content blockquote { border-left: 3px solid #df1283; padding-left: 1em; margin: 0.5em 0; color: #666; font-style: italic; }
                            .email-preview-content a { color: #0FFFE2; }
                            .email-preview-content img { max-width: 100%; height: auto; border-radius: 8px; }
                          </style>
                        ` + (bodyHtml
                          .replace(/\{\{first_name\}\}/g, '<span style="background: #fef08a; padding: 0 4px; border-radius: 2px;">Sarah</span>')
                          .replace(/\{\{email\}\}/g, '<span style="background: #fef08a; padding: 0 4px; border-radius: 2px;">sarah@example.com</span>')
                          .replace(/&#123;&#123;first_name&#125;&#125;/g, '<span style="background: #fef08a; padding: 0 4px; border-radius: 2px;">Sarah</span>')
                          .replace(/&#123;&#123;email&#125;&#125;/g, '<span style="background: #fef08a; padding: 0 4px; border-radius: 2px;">sarah@example.com</span>')
                          || '<p style="color: #999; font-style: italic;">Start typing to see preview...</p>')
                      }}
                    />
                    
                    {/* Footer */}
                    <div style={{ 
                      backgroundColor: '#fafafa', 
                      padding: '12px 16px', 
                      textAlign: 'center', 
                      borderTop: '1px solid #eeeeee' 
                    }}>
                      <p style={{ margin: '0 0 6px 0', color: '#666666', fontSize: '11px' }}>
                        Sent with 💜 from <span style={{ color: '#df1283', fontWeight: '600' }}>UnCloned® Journal</span>
                      </p>
                      <p style={{ margin: 0, color: '#999999', fontSize: '10px' }}>
                        © 2026 UnCloned® Journal. All rights reserved.
                      </p>
                      <p style={{ margin: '8px 0 0 0' }}>
                        <span style={{ color: '#999999', textDecoration: 'underline', fontSize: '10px' }}>
                          Unsubscribe from these emails
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-[#1a0527] px-3 py-2 border-t border-[#4A0A66]">
                  <p className="text-[10px] text-white/50 text-center">
                    ✓ Preview matches actual email appearance
                  </p>
                </div>
              </div>

              {/* Audience Preview */}
              <div className="bg-[#4A0A66]/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-white flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#0FFFE2]" />
                    Audience Preview
                  </h4>
                  <button
                    data-testid="bulk-refresh-preview"
                    aria-label="Refresh audience preview"
                    onClick={fetchPreview}
                    disabled={loadingPreview}
                    className="p-1 text-white/50 hover:text-white"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingPreview ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {loadingPreview ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#df1283]" />
                  </div>
                ) : preview ? (
                  <div className="space-y-4">
                    {/* Total Count */}
                    <div className="text-center py-4 bg-gradient-to-r from-[#df1283]/20 to-[#FF7901]/20 rounded-lg">
                      <div data-testid="bulk-preview-total" className="text-3xl font-bold text-white">{preview.total_recipients}</div>
                      <div className="text-sm text-white/60">
                        {testMode ? 'recipients (5 in test mode)' : 'recipients'}
                      </div>
                    </div>

                    {/* Tier Breakdown */}
                    {preview.tier_breakdown && Object.keys(preview.tier_breakdown).length > 0 && (
                      <div>
                        <p className="text-xs text-white/50 mb-2">By Tier</p>
                        <div data-testid="bulk-preview-tier-breakdown" className="space-y-1">
                          {Object.entries(preview.tier_breakdown).map(([tier, count]) => (
                            <div key={tier} className="flex items-center justify-between text-sm">
                              <span className="text-white/70 capitalize">{tier || 'Free'}</span>
                              <span className="text-white font-medium">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sample Users */}
                    {preview.sample_users && preview.sample_users.length > 0 && (
                      <div>
                        <p className="text-xs text-white/50 mb-2">Sample Recipients</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {preview.sample_users.map((user, idx) => (
                            <div key={idx} className="text-xs text-white/60 truncate">
                              {user.first_name || user.name?.split(' ')[0] || user.email?.split('@')[0]}
                              <span className="text-white/30 ml-1">({user.email})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-white/50">No preview available</p>
                    {getSegmentValidationMessage() && (
                      <p className="text-xs text-amber-400 mt-2 flex items-center justify-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {getSegmentValidationMessage()}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Validation Messages */}
              {!isSegmentValid() && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                  <p className="text-sm text-amber-400 flex items-center justify-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {getSegmentValidationMessage() || 'Please configure segment criteria'}
                  </p>
                </div>
              )}

              {isSegmentValid() && !isScheduleValid() && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center">
                  <p className="text-sm text-red-400 flex items-center justify-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {getScheduleValidationMessage()}
                  </p>
                </div>
              )}

              {/* Send Button */}
              <Button
                onClick={handleSend}
                disabled={sending || !isSegmentValid() || !isScheduleValid() || !preview || preview.total_recipients === 0}
                className={`w-full text-white h-12 disabled:opacity-50 ${
                  scheduleMode === 'scheduled' && isScheduleValid()
                    ? 'bg-gradient-to-r from-[#0FFFE2] to-[#00C9B7] text-black' 
                    : 'bg-gradient-to-r from-[#df1283] to-[#FF7901]'
                }`}
                data-testid="send-bulk-email-btn"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : scheduleMode === 'scheduled' ? (
                  <Clock className="w-5 h-5 mr-2" />
                ) : (
                  <Send className="w-5 h-5 mr-2" />
                )}
                {!isSegmentValid() 
                  ? 'Configure Segment First' 
                  : !isScheduleValid()
                    ? getScheduleValidationMessage()
                    : testMode 
                      ? 'Send Test Campaign' 
                      : scheduleMode === 'scheduled'
                        ? `Schedule for ${preview?.total_recipients || 0} Recipients`
                        : `Send to ${preview?.total_recipients || 0} Recipients`}
              </Button>

              {testMode && isSegmentValid() && (
                <p className="text-xs text-amber-400 text-center">
                  Test mode: Only first 5 recipients will receive this email
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkEmailCampaign;
